import { convertToRaw, EditorState } from 'draft-js';

import { FormInstance } from '@components/form/create-form';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { InfoBox } from '@components/info-box';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { placeholdersToBrackets } from '@components/text-editor/plugin-placeholder';
import { draftToHtml } from '@utils/draftjs';
import { BooleanKey } from '@utils/types';

type SettingsSwitchProps<T extends Record<string, any>> = {
  label: string;
  enabledText: string;
  disabledText: string;
  fieldId: BooleanKey<T>;
  isEnabled: boolean;
  form: FormInstance<T>;
  defaultValue?: boolean;
  dataCy?: string;
};

export const SettingsSwitch = <T extends Record<string, any>>({
  label,
  enabledText,
  disabledText,
  fieldId,
  isEnabled,
  form: Form,
  defaultValue,
  dataCy,
}: SettingsSwitchProps<T>) => {
  return (
    <Stack space={4} orientation="horizontal" align="center" justify="space-between">
      <div>
        <Text font="semibold14" text={label} />
        {isEnabled ? (
          <Text color="textPrimary" font="regular14" text={enabledText} />
        ) : (
          <Text color="textHelper" font="regular14" text={disabledText} />
        )}
      </div>
      <Form.Switch
        id={fieldId}
        label={label}
        labelVisible={false}
        defaultValue={defaultValue ?? false}
        dataCy={dataCy}
      />
    </Stack>
  );
};

export const SettingsTitle = ({ title, description }: { title: string; description?: string }) => {
  return (
    <Stack space={2}>
      <Text font="bold18" color="purple100" text={title} />
      {description && <Text font="regular14" color="purple80" text={description} />}
    </Stack>
  );
};

export const convertFromEditorStateToHtml = (editorState: EditorState) => {
  const rawData = convertToRaw(editorState.getCurrentContent());
  const converted = placeholdersToBrackets(rawData);
  return draftToHtml(converted);
};

export const SettingsInfoBox = ({ text }: { text: string }) => {
  return (
    <InfoBox variant="indigo">
      <Stack orientation="horizontal" space={2} align="center">
        <IconFormIndicatorInfo color="elementIndigo100" />
        <Text font="regular14" text={text} />
      </Stack>
    </InfoBox>
  );
};
